import { useAuth } from '../App';
import { Link } from 'react-router-dom';
import { 
  Clock, WashingMachine, Scale, Route, AlertTriangle, 
  ChevronRight, TrendingUp, TrendingDown,
  BarChart3, CheckCircle, Loader2, CircleAlert, Activity,
  FileSpreadsheet, ArrowRight, ShieldAlert, Bell, X
} from 'lucide-react';
import { 
  getDashboardKpisV2, 
  DashboardKpisV2, 
  DashboardRangeType,
  MachineKpiRow,
  LineKpiRow,
  getCorrectionRequestDetail,
  updateCorrectionRequestStatus,
  CorrectionRequestDetail,
  resolveCorrectionRequestV2,
  ItemResolutionInput,
  simulateOverride,
  SimulateOverrideResponse,
} from '../api/client';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { HorizontalBarChart } from '../components/BarChart';
import { getChartLabelForMachine, getChartLabelForLine } from '../utils/chart-labels';

/**
 * Dashboard Page - Phase 1: Admin-Dashboard
 * 
 * Features:
 * - Zeitraum-Auswahl (Heute, Aktuelle Schicht, Gestern, Letzte Schicht)
 * - Badge: "Laufend (Teilzeitraum)" vs "Abgeschlossen"
 * - KPI-Row mit WSA, Processing, Störungen, Datenqualität
 * - Operative Tabelle nach Maschinen/Lines
 * - Shortcuts zu Auswertungen
 * 
 * WICHTIG: Verwendet AUSSCHLIESSLICH bestehende shared Zeitlogik via API.
 */
export default function DashboardPage() {
  const { user } = useAuth();
  const [selectedRange, setSelectedRange] = useState<DashboardRangeType>('today');
  const [data, setData] = useState<DashboardKpisV2 | null>(null);
  const [dataQuality, setDataQuality] = useState<{
    shiftsWithMissingTwa: number;
    totalShiftsLast7Days: number;
    disruptionsWithoutMachine: number;
    totalDisruptions: number;
    openDisruptionsOver24h: number;
    openDisruptionsOver48h: number;
    disruptionsWithoutCategory: number;
    stillstandTodayMinutes: number;
    openCorrections: OpenCorrection[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (range: DashboardRangeType) => {
    setLoading(true);
    setError(null);
    try {
      const [result, qualityResult] = await Promise.all([
        getDashboardKpisV2(range),
        fetch('/api/dashboard/data-quality', { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      ]);
      setData(result);
      setDataQuality(qualityResult);
    } catch (err) {
      setError('Fehler beim Laden der Dashboard-Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && ['ADMIN', 'SUPERADMIN', 'SYSTEM'].includes(user.role)) {
      loadData(selectedRange);
    } else {
      setLoading(false);
    }
  }, [user, selectedRange, loadData]);

  const handleRangeChange = (range: DashboardRangeType) => {
    setSelectedRange(range);
  };

  // Correction Modal State
  const [correctionModal, setCorrectionModal] = useState<{
    open: boolean;
    loading: boolean;
    saving: boolean;
    detail: CorrectionRequestDetail | null;
    error: string;
    success: string;
  }>({ open: false, loading: false, saving: false, detail: null, error: '', success: '' });
  const [v2Resolutions, setV2Resolutions] = useState<Record<string, 'ACCEPTED' | 'REJECTED'>>({});
  const [adminComment, setAdminComment] = useState('');

  const openCorrectionModal = async (correctionId: string) => {
    setCorrectionModal({ open: true, loading: true, saving: false, detail: null, error: '', success: '' });
    setV2Resolutions({});
    setAdminComment('');
    try {
      const detail = await getCorrectionRequestDetail(correctionId);
      const initial: Record<string, 'ACCEPTED' | 'REJECTED'> = {};
      if (detail.correctionItems && Array.isArray(detail.correctionItems)) {
        detail.correctionItems.forEach((item) => { initial[item.field] = 'ACCEPTED'; });
      }
      setV2Resolutions(initial);
      setCorrectionModal(prev => ({ ...prev, loading: false, detail }));
    } catch {
      setCorrectionModal(prev => ({ ...prev, loading: false, error: 'Fehler beim Laden der Korrekturanfrage' }));
    }
  };

  const handleCorrectionResolve = async () => {
    if (!correctionModal.detail) return;
    setCorrectionModal(prev => ({ ...prev, saving: true, error: '' }));
    try {
      const itemResolutions: ItemResolutionInput[] = Object.entries(v2Resolutions).map(([field, resolution]) => ({
        field,
        resolution,
      }));
      await resolveCorrectionRequestV2(correctionModal.detail.id, {
        itemResolutions,
        adminComment: adminComment.trim() || undefined,
      });
      setCorrectionModal(prev => ({ ...prev, saving: false, success: 'Korrektur bearbeitet' }));
      setTimeout(() => {
        setCorrectionModal({ open: false, loading: false, saving: false, detail: null, error: '', success: '' });
        loadData(selectedRange);
      }, 1000);
    } catch (err: any) {
      setCorrectionModal(prev => ({
        ...prev,
        saving: false,
        error: err?.message || 'Fehler beim Speichern',
      }));
    }
  };

  if (!user) {
    return null;
  }

  const isAdmin = ['ADMIN', 'SUPERADMIN', 'SYSTEM'].includes(user.role);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* A) HEADER / KONTEXT */}
        <DashboardHeader
          rangeInfo={data?.range}
          selectedRange={selectedRange}
          onRangeChange={handleRangeChange}
          loading={loading}
        />

        {/* Fehleranzeige */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* B) ALARM BANNER */}
        {isAdmin && (
          <AlertBanner
            activeDisruptions={data?.kpis.activeDisruptions ?? 0}
            openCorrectionsCount={dataQuality?.openCorrections?.length ?? 0}
            openDisruptionsOver24h={dataQuality?.openDisruptionsOver24h ?? 0}
            openDisruptionsOver48h={dataQuality?.openDisruptionsOver48h ?? 0}
            loading={loading}
          />
        )}

        {/* C) OFFENE KORREKTUREN (Arbeitsliste) */}
        {isAdmin && (
          <CorrectionWorkList
            openCorrections={dataQuality?.openCorrections ?? []}
            loading={loading}
            onOpenCorrection={openCorrectionModal}
          />
        )}

        {/* D) KPI-ROW */}
        {isAdmin && (
          <KpiRow data={data} loading={loading} />
        )}

        {/* E) CHARTS - Top 5 Maschinen & Strecken */}
        {isAdmin && data && (
          <ChartsSection 
            machineRows={data.machineRows} 
            lineRows={data.lineRows}
            loading={loading}
          />
        )}

        {/* F) DRILLDOWN CARDS */}
        {isAdmin && (
          <DrilldownCards />
        )}

        {/* G) DATA QUALITY (eingeklappt) */}
        {isAdmin && dataQuality && (
          <DataQualityBox data={dataQuality} />
        )}
      </div>

      {/* Korrektur-Modal */}
      <CorrectionResolveModal
        modal={correctionModal}
        v2Resolutions={v2Resolutions}
        adminComment={adminComment}
        onSetResolution={(field, val) => setV2Resolutions(prev => ({ ...prev, [field]: val }))}
        onSetAdminComment={setAdminComment}
        onResolve={handleCorrectionResolve}
        onClose={() => setCorrectionModal({ open: false, loading: false, saving: false, detail: null, error: '', success: '' })}
      />
    </AdminLayout>
  );
}

// =============================================================================
// Sub-Components
// =============================================================================

// A) HEADER mit Zeitraum-Auswahl und Badge
interface DashboardHeaderProps {
  rangeInfo?: DashboardKpisV2['range'];
  selectedRange: DashboardRangeType;
  onRangeChange: (range: DashboardRangeType) => void;
  loading: boolean;
}

const RANGE_OPTIONS: { value: DashboardRangeType; label: string }[] = [
  { value: 'today', label: 'Heute' },
  { value: 'currentShift', label: 'Aktuelle Schicht' },
  { value: 'yesterday', label: 'Gestern' },
  { value: 'lastShift', label: 'Letzte Schicht' },
];

function DashboardHeader({ rangeInfo, selectedRange, onRangeChange, loading }: DashboardHeaderProps) {
  // Stand-Zeit in Europe/Berlin
  const standTime = new Date().toLocaleTimeString('de-DE', { 
    hour: '2-digit', 
    minute: '2-digit',
    timeZone: 'Europe/Berlin'
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            {new Date().toLocaleDateString('de-DE', { 
              timeZone: 'Europe/Berlin',
              weekday: 'long', 
              day: 'numeric', 
              month: 'long',
              year: 'numeric'
            })}
            {rangeInfo?.shiftName && (
              <span className="ml-2 text-slate-400">
                | {rangeInfo.shiftName}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Stand-Zeit */}
          <span className="text-xs text-slate-400">
            Stand: {standTime}
          </span>
          {/* Status Badge */}
          {rangeInfo && (
            <div className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 ${
              rangeInfo.isPartial 
                ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                : 'bg-green-100 text-green-800 border border-green-200'
            }`}>
              {rangeInfo.isPartial ? (
                <>
                  <Activity className="w-4 h-4 animate-pulse" />
                  Laufend (Teilzeitraum)
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Abgeschlossen
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Zeitraum-Pills */}
      <div className="flex flex-wrap gap-2">
        {RANGE_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => onRangeChange(option.value)}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedRange === option.value
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// B) KPI-ROW
interface KpiRowProps {
  data: DashboardKpisV2 | null;
  loading: boolean;
}

function KpiRow({ data, loading }: KpiRowProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border p-4 animate-pulse">
            <div className="h-4 bg-slate-200 rounded w-24 mb-2" />
            <div className="h-8 bg-slate-200 rounded w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const { kpis, comparison } = data;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <Link to="/reports/wsa">
        <KpiCard
          label="WSA Waschgaenge"
          value={kpis.wsaCount}
          icon={WashingMachine}
          trend={comparison?.wsaKgDelta}
        />
      </Link>
      <Link to="/reports/wsa">
        <KpiCard
          label="WSA Gewicht"
          value={`${kpis.wsaKg.toLocaleString('de-DE')} kg`}
          icon={Scale}
          trend={comparison?.wsaKgDelta}
        />
      </Link>
      <Link to="/reports/processing">
        <KpiCard
          label="Strecken Input"
          value={`${kpis.processingKg.toLocaleString('de-DE')} kg`}
          icon={Route}
          trend={comparison?.processingKgDelta}
        />
      </Link>
      <Link to="/reports/disruptions?status=open">
        <KpiCard
          label="Aktive Stoerungen"
          value={kpis.activeDisruptions}
          icon={AlertTriangle}
          alert={kpis.activeDisruptions > 0}
          subtitle={kpis.oldestDisruptionMinutes 
            ? `Aelteste: ${Math.floor(kpis.oldestDisruptionMinutes / 60)}h ${kpis.oldestDisruptionMinutes % 60}min` 
            : undefined}
        />
      </Link>
      <div className="bg-white rounded-lg border p-4">
        <p className="text-sm font-medium text-slate-500">Datenqualitaet</p>
        <div className="mt-2 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Maschinen</span>
            <span className="font-medium">{kpis.machinesActive} / {kpis.machinesTotal}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Strecken</span>
            <span className="font-medium">{kpis.linesActive} / {kpis.linesTotal}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// KPI Card Component
interface KpiCardProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  trend?: number | null;
  alert?: boolean;
  subtitle?: string;
}

function KpiCard({ label, value, icon: Icon, trend, alert, subtitle }: KpiCardProps) {
  return (
    <div className={`bg-white rounded-lg border p-4 hover:bg-slate-50 transition-colors h-full ${alert ? 'border-red-200 bg-red-50' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alert ? 'bg-red-100' : 'bg-slate-100'}`}>
          <Icon className={`w-5 h-5 ${alert ? 'text-red-600' : 'text-slate-600'}`} />
        </div>
      </div>
      {trend !== undefined && trend !== null && (
        <div className="mt-2 flex items-center gap-1 text-sm">
          {trend >= 0 ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={trend >= 0 ? 'text-green-600' : 'text-red-600'}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
          <span className="text-slate-400">vs. Vortag</span>
        </div>
      )}
    </div>
  );
}

// C) ALARM BANNER - Aggregierte Alarm-Kacheln
interface OpenCorrection {
  id: string;
  targetType: string;
  targetId: string;
  category: string;
  reasonText: string;
  workerComment: string | null;
  correctionItems: Array<{ field: string; oldValue: unknown; newValue: unknown; oldValueLabel?: string; newValueLabel?: string }> | null;
  createdAt: string;
  contextName: string;
  entryContext: {
    lineName?: string;
    subLineName?: string;
    machineName?: string;
    programName?: string;
    customerName?: string;
    customerGroupName?: string;
    weightKg?: number;
    recordedAt?: string;
    recordedByName?: string;
    laundryClassName?: string;
    articleGroupName?: string;
  };
}

interface AlertBannerProps {
  activeDisruptions: number;
  openCorrectionsCount: number;
  openDisruptionsOver24h: number;
  openDisruptionsOver48h: number;
  loading: boolean;
}

function AlertBanner({ activeDisruptions, openCorrectionsCount, openDisruptionsOver24h, openDisruptionsOver48h, loading }: AlertBannerProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="rounded-lg border p-4 animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-12 mb-1" />
            <div className="h-4 bg-slate-200 rounded w-32" />
          </div>
        ))}
      </div>
    );
  }

  const hasAlarms = activeDisruptions > 0 || openCorrectionsCount > 0 || openDisruptionsOver24h > 0;

  if (!hasAlarms) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
        <CheckCircle className="w-5 h-5 text-green-500" />
        <span className="text-sm font-medium text-green-800">Keine offenen Meldungen</span>
      </div>
    );
  }

  const tiles: Array<{
    count: number;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: 'red' | 'amber';
    href?: string;
  }> = [];

  if (activeDisruptions > 0) {
    tiles.push({ count: activeDisruptions, label: 'Stoerungen aktiv', icon: AlertTriangle, color: 'red', href: '/reports/disruptions?status=open' });
  }
  if (openCorrectionsCount > 0) {
    tiles.push({ count: openCorrectionsCount, label: 'Korrekturanfragen offen', icon: CircleAlert, color: 'amber' });
  }
  if (openDisruptionsOver48h > 0) {
    tiles.push({ count: openDisruptionsOver48h, label: 'Stoerungen >48h offen', icon: ShieldAlert, color: 'red' });
  } else if (openDisruptionsOver24h > 0) {
    tiles.push({ count: openDisruptionsOver24h, label: 'Stoerungen >24h offen', icon: Clock, color: 'amber' });
  }

  const colorStyles = {
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-600', countBg: 'bg-red-100' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: 'text-amber-600', countBg: 'bg-amber-100' },
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-5 h-5 text-slate-700" />
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Aufmerksamkeit erforderlich</h2>
      </div>
      <div className={`grid gap-3 ${tiles.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4'}`}>
        {tiles.map((tile) => {
          const styles = colorStyles[tile.color];
          const Icon = tile.icon;
          const content = (
            <div key={tile.label} className={`${styles.bg} ${styles.border} border rounded-lg p-3 flex items-center gap-3 transition-colors ${tile.href ? 'hover:shadow-sm' : ''}`}>
              <div className={`${styles.countBg} w-10 h-10 rounded-lg flex items-center justify-center shrink-0`}>
                <span className={`text-lg font-bold ${styles.text}`}>{tile.count}</span>
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <Icon className={`w-4 h-4 shrink-0 ${styles.icon}`} />
                <span className={`text-sm font-medium ${styles.text}`}>{tile.label}</span>
              </div>
            </div>
          );
          return tile.href ? (
            <Link key={tile.label} to={tile.href}>{content}</Link>
          ) : (
            <div key={tile.label}>{content}</div>
          );
        })}
      </div>
    </div>
  );
}

// D) CORRECTION WORK LIST - Kompakte Diff-Arbeitsliste
interface CorrectionWorkListProps {
  openCorrections: OpenCorrection[];
  loading: boolean;
  onOpenCorrection: (correctionId: string) => void;
}

function CorrectionWorkList({ openCorrections, loading, onOpenCorrection }: CorrectionWorkListProps) {
  // K1 Korrektur-Umbau: Der Eingang zeigt Inhalt (Vorher -> Nachher), Grund und
  // Alter jedes Antrags. Altformat-Antraege ohne Aenderungszeilen (Februar-Aera)
  // sind direkt schliessbar - sie waren weder pruefbar noch loeschbar (#23).
  const [geschlossen, setGeschlossen] = useState<string[]>([]);
  const [schliesse, setSchliesse] = useState<string | null>(null);
  const [schliessFehler, setSchliessFehler] = useState<string | null>(null);

  const FELD_LABELS: Record<string, string> = {
    lineId: 'Strecke', subLineId: 'Substrecke', customerId: 'Kunde',
    customerGroupId: 'Kundengruppe', weightKg: 'Gewicht', machineId: 'Maschine',
    programId: 'Programm', laundryClassId: 'Wäscheklasse',
    articleGroupId: 'Artikelgruppe', shiftDate: 'Schichtdatum', shiftTypeCodeSnap: 'Schicht',
  };

  const alterLabel = (createdAt: string): { text: string; alt: boolean } => {
    const d = new Date(createdAt);
    const tage = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (tage <= 0) return { text: `heute ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`, alt: false };
    if (tage === 1) return { text: 'gestern', alt: false };
    return {
      text: `seit ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} · ${tage} Tage`,
      alt: tage >= 7,
    };
  };

  const schliesseAltformat = async (c: OpenCorrection) => {
    setSchliesse(c.id);
    setSchliessFehler(null);
    try {
      await updateCorrectionRequestStatus(c.id, 'REJECTED', 'Altformat ohne Änderungszeilen — ohne Wirkung geschlossen');
      setGeschlossen(prev => [...prev, c.id]);
    } catch {
      setSchliessFehler(c.id);
    } finally {
      setSchliesse(null);
    }
  };

  const sichtbar = openCorrections.filter(c => !geschlossen.includes(c.id));

  const renderCorrectionRow = (c: OpenCorrection) => {
    const ctx = c.entryContext || {};
    const items = c.correctionItems;
    const istAltformat = !items || items.length === 0;
    const contextLabel = ctx.lineName || ctx.machineName || '—';
    const alter = alterLabel(c.createdAt);
    const grund = c.workerComment || c.reasonText || '—';

    const diffText = !istAltformat
      ? items!.map(i => {
          const feld = FELD_LABELS[i.field] || i.field;
          const von = i.oldValueLabel ?? String(i.oldValue ?? '—');
          const zu = i.newValueLabel ?? String(i.newValue ?? '—');
          return `${feld}: ${von} → ${zu}`;
        }).join('  ·  ')
      : null;

    const inhalt = (
      <>
        <div className="flex items-center justify-between mb-1.5 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {c.targetType === 'PROCESSING_ENTRY' ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded shrink-0">
                <Route className="w-3 h-3" />
                Vorarbeiter
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded shrink-0">
                <WashingMachine className="w-3 h-3" />
                WSA
              </span>
            )}
            <span className="text-sm font-medium text-slate-900 truncate">{contextLabel}</span>
            {ctx.customerName && (
              <span className="text-xs text-slate-500 truncate">{ctx.customerName}</span>
            )}
            {ctx.weightKg != null && (
              <span className="text-xs text-slate-400 shrink-0">{ctx.weightKg.toLocaleString('de-DE')} kg</span>
            )}
          </div>
          <span
            className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full border ${
              alter.alt
                ? 'bg-amber-50 border-amber-300 text-amber-700 font-medium'
                : 'bg-slate-50 border-slate-200 text-slate-500'
            }`}
          >
            {alter.text}
          </span>
        </div>

        {diffText ? (
          <div className="text-xs text-slate-700 bg-slate-50 border-l-2 border-blue-400 rounded-r px-2 py-1 mb-1">
            {diffText}
          </div>
        ) : (
          <div className="text-xs text-amber-800 bg-amber-50 border-l-2 border-amber-400 rounded-r px-2 py-1 mb-1">
            Altformat ohne Änderungszeilen — nicht prüfbar. Freitext: „{c.reasonText || '—'}"
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400 truncate">Grund: {grund}</span>
          {istAltformat ? (
            <button
              onClick={(e) => { e.stopPropagation(); void schliesseAltformat(c); }}
              disabled={schliesse === c.id}
              className="shrink-0 text-[11px] font-medium px-2 py-1 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
            >
              {schliesse === c.id ? 'Schließe …' : 'Ohne Wirkung schließen'}
            </button>
          ) : (
            <span className="shrink-0 text-[11px] text-blue-600">prüfen ›</span>
          )}
        </div>
        {schliessFehler === c.id && (
          <div className="mt-1 text-[11px] text-red-600">Schließen fehlgeschlagen — bitte erneut versuchen.</div>
        )}
      </>
    );

    // Altformat: kein Klickziel (die Pruefung wuerde nur den Fehler zeigen)
    if (istAltformat) {
      return (
        <div key={c.id} className="w-full text-left rounded-lg border border-amber-200 p-3 bg-white">
          {inhalt}
        </div>
      );
    }
    return (
      <button
        key={c.id}
        onClick={() => onOpenCorrection(c.id)}
        className="w-full text-left rounded-lg border border-slate-200 hover:border-amber-300 hover:shadow-sm cursor-pointer transition-all p-3 bg-white"
      >
        {inhalt}
      </button>
    );
  };

  if (loading || sichtbar.length === 0) {
    if (loading) return null;
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <FileSpreadsheet className="w-5 h-5 text-slate-700" />
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Offene Korrekturen</h2>
        <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">{sichtbar.length}</span>
      </div>
      <div className="space-y-2">
        {sichtbar.map(renderCorrectionRow)}
      </div>
    </div>
  );
}

// Korrektur-Bearbeitungs-Modal
interface CorrectionResolveModalProps {
  modal: {
    open: boolean;
    loading: boolean;
    saving: boolean;
    detail: CorrectionRequestDetail | null;
    error: string;
    success: string;
  };
  v2Resolutions: Record<string, 'ACCEPTED' | 'REJECTED'>;
  adminComment: string;
  onSetResolution: (field: string, val: 'ACCEPTED' | 'REJECTED') => void;
  onSetAdminComment: (val: string) => void;
  onResolve: () => void;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  lineId: 'Strecke', subLineId: 'Substrecke', customerId: 'Kunde',
  customerGroupId: 'Kundengruppe', weightKg: 'Gewicht',
  laundryClassId: 'Waescheklasse', articleGroupId: 'Artikelgruppe',
  machineId: 'Maschine', programId: 'Programm',
};

// Sub-Komponente: Feld-Zeile fuer Original / Vorschau Panels
function EntryFieldRow({ label, value, changed, error }: {
  label: string;
  value: string | null | undefined;
  changed?: boolean;
  error?: string;
}) {
  // Zeile ausblenden nur wenn kein Wert, kein Fehler UND nicht explizit null (cleared)
  if (value === undefined && !error) return null;
  return (
    <div className={`py-1.5 ${error ? 'bg-red-50 -mx-3 px-3 rounded' : ''}`}>
      <div className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-medium ${
        error ? 'text-red-600' : changed ? 'text-green-700' : 'text-gray-900'
      }`}>
        {error ? error : value || '—'}
      </div>
    </div>
  );
}

// Sub-Komponente: Entry-Kontext Panel (fuer Links + Rechts)
function EntryContextPanel({ ctx, title, bgClass, changedFields, validationErrors }: {
  ctx: CorrectionRequestDetail['entryContext'];
  title: string;
  bgClass: string;
  changedFields?: Set<string>;
  validationErrors?: Record<string, string>;
}) {
  const isProcessing = !!(ctx.lineName || ctx.subLineName);
  const formatWeight = (w?: number) => w != null ? `${w.toLocaleString('de-DE')} kg` : undefined;
  const formatTime = (d?: string) => d ? new Date(d).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' }) : undefined;

  return (
    <div className={`${bgClass} rounded-lg p-3 h-full`}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</div>
      <div className="space-y-0.5">
        {isProcessing ? (
          <>
            <EntryFieldRow label="Strecke" value={ctx.lineName} changed={changedFields?.has('lineId')} error={validationErrors?.lineId} />
            <EntryFieldRow label="Substrecke" value={ctx.subLineName} changed={changedFields?.has('subLineId')} error={validationErrors?.subLineId} />
          </>
        ) : (
          <>
            <EntryFieldRow label="Maschine" value={ctx.machineName} changed={changedFields?.has('machineId')} error={validationErrors?.machineId} />
            <EntryFieldRow label="Programm" value={ctx.programName} changed={changedFields?.has('programId')} error={validationErrors?.programId} />
          </>
        )}
        <EntryFieldRow label="Kunde" value={ctx.customerName || ctx.customerGroupName} changed={changedFields?.has('customerId') || changedFields?.has('customerGroupId')} error={validationErrors?.customerId} />
        <EntryFieldRow label="Gewicht" value={formatWeight(ctx.weightKg)} changed={changedFields?.has('weightKg')} error={validationErrors?.weightKg} />
        <EntryFieldRow label="Waescheklasse" value={ctx.laundryClassName} changed={changedFields?.has('laundryClassId')} error={validationErrors?.laundryClassId} />
        <EntryFieldRow label="Artikelgruppe" value={ctx.articleGroupName} changed={changedFields?.has('articleGroupId')} error={validationErrors?.articleGroupId} />
        <EntryFieldRow label="Schicht" value={ctx.shiftRuleNameSnap} />
        <EntryFieldRow label="Erfasst" value={formatTime(ctx.recordedAt)} />
        <EntryFieldRow label="Erfasst von" value={ctx.recordedByName} />
      </div>
    </div>
  );
}

function CorrectionResolveModal({ modal, v2Resolutions, adminComment, onSetResolution, onSetAdminComment, onResolve, onClose }: CorrectionResolveModalProps) {
  const detail = modal.detail;
  const isV2 = detail?.correctionItems && Array.isArray(detail.correctionItems) && detail.correctionItems.length > 0;
  const items = isV2
    ? (detail!.correctionItems as Array<{ field: string; oldValue: unknown; newValue: unknown; oldValueLabel?: string; newValueLabel?: string }>)
    : [];

  // Backend-Simulation: identische Engine wie Resolve/Override-Write
  const [simulation, setSimulation] = useState<SimulateOverrideResponse | null>(null);
  const [simLoading, setSimLoading] = useState(false);

  // Simulation bei jeder Resolution-Aenderung neu laden
  useEffect(() => {
    if (!detail || !isV2) return;

    // Baue proposedDelta aus akzeptierten Items
    const proposedDelta: Record<string, unknown> = {};
    let hasAccepted = false;
    for (const item of items) {
      if (v2Resolutions[item.field] === 'ACCEPTED') {
        proposedDelta[item.field] = item.newValue;
        hasAccepted = true;
      }
    }

    if (!hasAccepted) {
      setSimulation(null);
      return;
    }

    let cancelled = false;
    setSimLoading(true);
    simulateOverride(
      detail.targetType as 'WSA_ENTRY' | 'PROCESSING_ENTRY',
      detail.targetId,
      proposedDelta,
    ).then(result => {
      if (!cancelled) setSimulation(result);
    }).catch(() => {
      if (!cancelled) setSimulation(null);
    }).finally(() => {
      if (!cancelled) setSimLoading(false);
    });

    return () => { cancelled = true; };
  }, [detail, isV2, items, v2Resolutions]);

  // Live-Vorschau: Original + Backend-Simulation zusammenfuehren
  const previewCtx = useMemo(() => {
    if (!detail) return {} as CorrectionRequestDetail['entryContext'];
    const base = { ...detail.entryContext };
    if (!isV2) return base;

    const fieldToCtxKey: Record<string, keyof typeof base> = {
      lineId: 'lineName',
      subLineId: 'subLineName',
      machineId: 'machineName',
      programId: 'programName',
      customerId: 'customerName',
      customerGroupId: 'customerGroupName',
      laundryClassId: 'laundryClassName',
      articleGroupId: 'articleGroupName',
    };

    // Schritt 1: Akzeptierte Items mergen (Labels fuer Anzeige)
    for (const item of items) {
      const resolution = v2Resolutions[item.field];
      if (resolution === 'ACCEPTED') {
        if (item.field === 'weightKg') {
          base.weightKg = Number(item.newValue);
        } else {
          const ctxKey = fieldToCtxKey[item.field];
          if (ctxKey) {
            (base as Record<string, unknown>)[ctxKey] = item.newValueLabel || String(item.newValue ?? '—');
          }
        }
      }
    }

    // Schritt 2: Auto-Cleared Felder aus Backend-Simulation auf null setzen
    if (simulation?.autoCleared) {
      for (const field of simulation.autoCleared) {
        const ctxKey = fieldToCtxKey[field];
        if (ctxKey) {
          (base as Record<string, unknown>)[ctxKey] = null;
        }
      }
    }

    return base;
  }, [detail, isV2, items, v2Resolutions, simulation]);

  // Geaenderte Felder fuer Vorschau-Hervorhebung
  const changedFields = useMemo(() => {
    const set = new Set<string>();
    if (!isV2) return set;
    for (const item of items) {
      if (v2Resolutions[item.field] === 'ACCEPTED') {
        set.add(item.field);
      }
    }
    // Auto-cleared Felder auch als geaendert markieren
    if (simulation?.autoCleared) {
      for (const field of simulation.autoCleared) {
        set.add(field);
      }
    }
    return set;
  }, [isV2, items, v2Resolutions, simulation]);

  // Validierung: aus Backend-Simulation + lokale Checks
  const validationErrors = useMemo((): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!detail || !isV2) return errors;

    // Backend-Validierungsfehler (aus Engine)
    if (simulation?.validationError) {
      const code = simulation.validationError.code;
      if (code === 'MISSING_LAUNDRY_CLASS') {
        errors.laundryClassId = 'Waescheklasse ist fuer diese Strecke erforderlich';
      } else if (code === 'INVALID_LAUNDRY_CLASS') {
        errors.laundryClassId = 'Waescheklasse ist fuer diese Strecke nicht erlaubt';
      } else if (code === 'MISSING_ARTICLE_GROUP') {
        errors.articleGroupId = 'Artikelgruppe ist fuer diese Strecke erforderlich';
      } else if (code === 'INVALID_ARTICLE_GROUP_NOT_ALLOWED') {
        errors.articleGroupId = 'Artikelgruppe ist fuer diese Strecke nicht erlaubt';
      }
    }

    return errors;
  }, [detail, isV2, simulation]);

  if (!modal.open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl ${isV2 ? 'max-w-6xl' : 'max-w-lg'} w-full max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Korrekturanfrage bearbeiten</h2>
            {detail && (
              <div className="flex items-center gap-2">
                {detail.targetType === 'PROCESSING_ENTRY' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                    <Route className="w-3 h-3" />
                    Vorarbeiter
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
                    <WashingMachine className="w-3 h-3" />
                    WSA
                  </span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Loading */}
          {modal.loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}

          {/* Error */}
          {modal.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">
              {modal.error}
            </div>
          )}

          {/* Success */}
          {modal.success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
              {modal.success}
            </div>
          )}

          {detail && !modal.success && (
            <>
              {isV2 ? (
                /* ===== 3-SPALTEN LAYOUT (V2) ===== */
                <div className="grid grid-cols-3 gap-4">
                  {/* LINKS: Original (IST) */}
                  <EntryContextPanel
                    ctx={detail.entryContext}
                    title="Original (IST)"
                    bgClass="bg-slate-50"
                  />

                  {/* MITTE: Korrektur-Items */}
                  <div className="bg-amber-50 rounded-lg p-3 h-full">
                    <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">Angeforderte Korrekturen</div>
                    <div className="space-y-2">
                      {items.map((item) => {
                        const label = FIELD_LABELS[item.field] || item.field;
                        const oldDisplay = item.oldValueLabel || String(item.oldValue ?? '—');
                        const newDisplay = item.newValueLabel || String(item.newValue ?? '—');
                        const resolution = v2Resolutions[item.field] || 'ACCEPTED';
                        return (
                          <div key={item.field} className={`p-2.5 rounded-lg border transition-colors ${
                            resolution === 'ACCEPTED'
                              ? 'bg-green-50 border-green-300'
                              : 'bg-white border-red-300'
                          }`}>
                            <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
                            <div className="text-sm mb-2">
                              <span className="text-gray-400 line-through">{oldDisplay}</span>
                              <span className="text-gray-400 mx-1">&rarr;</span>
                              <span className="font-semibold text-gray-900">{newDisplay}</span>
                            </div>
                            <div className="flex gap-1.5">
                              <button
                                type="button"
                                onClick={() => onSetResolution(item.field, 'ACCEPTED')}
                                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                  resolution === 'ACCEPTED'
                                    ? 'bg-green-600 text-white shadow-sm'
                                    : 'bg-white border border-gray-300 text-gray-600 hover:bg-green-50 hover:border-green-300'
                                }`}
                              >
                                Annehmen
                              </button>
                              <button
                                type="button"
                                onClick={() => onSetResolution(item.field, 'REJECTED')}
                                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${
                                  resolution === 'REJECTED'
                                    ? 'bg-red-600 text-white shadow-sm'
                                    : 'bg-white border border-gray-300 text-gray-600 hover:bg-red-50 hover:border-red-300'
                                }`}
                              >
                                Ablehnen
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Worker-Grund */}
                    {detail.workerComment && (
                      <div className="mt-3 pt-2 border-t border-amber-200 text-xs text-amber-700">
                        <span className="font-medium">Grund:</span> {detail.workerComment}
                      </div>
                    )}
                    {/* Melder-Info */}
                    {detail.createdByStation && (
                      <div className="mt-2 text-[11px] text-gray-400">
                        Gemeldet von: {detail.createdByStation.name}
                        {detail.createdAt && (
                          <span> am {new Date(detail.createdAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* RECHTS: Live-Vorschau (SOLL) */}
                  <EntryContextPanel
                    ctx={previewCtx}
                    title="Vorschau (SOLL)"
                    bgClass="bg-green-50"
                    changedFields={changedFields}
                    validationErrors={validationErrors}
                  />
                </div>
              ) : (
                /* ===== LEGACY LAYOUT (v1) ===== */
                <div className="space-y-4">
                  <EntryContextPanel
                    ctx={detail.entryContext}
                    title="Erfasster Eintrag"
                    bgClass="bg-slate-50"
                  />
                  {detail.reasonText && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                      <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                        <AlertTriangle className="w-4 h-4" />
                        Gemeldeter Grund
                      </div>
                      <div className="text-amber-600">{detail.reasonText}</div>
                    </div>
                  )}
                  {detail.createdByStation && (
                    <div className="text-xs text-gray-400">
                      Gemeldet von: {detail.createdByStation.name}
                      {detail.createdAt && (
                        <span> am {new Date(detail.createdAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Admin-Kommentar (unter den Spalten) */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin-Kommentar (optional)</label>
                <textarea
                  value={adminComment}
                  onChange={(e) => onSetAdminComment(e.target.value)}
                  placeholder="Optionaler Kommentar zur Entscheidung"
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                />
              </div>

              {/* Validierungswarnungen (aus Backend-Engine) */}
              {Object.keys(validationErrors).length > 0 && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {simulation?.validationError?.message || 'Validierungshinweise in der Vorschau beachten'}
                </div>
              )}
              {/* Auto-Clear Hinweis */}
              {simulation?.autoCleared && simulation.autoCleared.length > 0 && (
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-700 flex items-center gap-2">
                  <CircleAlert className="w-3.5 h-3.5 shrink-0" />
                  {simulation.autoCleared.map(f => FIELD_LABELS[f] || f).join(', ')} {simulation.autoCleared.length === 1 ? 'wird' : 'werden'} automatisch entfernt (nicht zutreffend fuer Zielstrecke)
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {detail && !modal.success && (
          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-xl shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Abbrechen
            </button>
            {isV2 ? (() => {
              // K2: Der Knopf sagt, was passiert — und Komplett-Ablehnung braucht einen Satz
              const angenommen = Object.values(v2Resolutions).filter(v => v === 'ACCEPTED').length;
              const alleAbgelehnt = Object.keys(v2Resolutions).length > 0 && angenommen === 0;
              const begruendungFehlt = alleAbgelehnt && adminComment.trim().length < 5;
              return (
                <div className="flex items-center gap-3">
                  {begruendungFehlt && (
                    <span className="text-xs text-red-600">Komplett-Ablehnung braucht einen Satz im Kommentar.</span>
                  )}
                  <button
                    onClick={onResolve}
                    disabled={modal.saving || Object.keys(v2Resolutions).length === 0 || Object.keys(validationErrors).length > 0 || simLoading || begruendungFehlt}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${alleAbgelehnt ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {modal.saving ? 'Speichern...' : simLoading ? 'Validierung...'
                      : alleAbgelehnt ? 'Alles ablehnen'
                      : `${angenommen} ${angenommen === 1 ? 'Änderung' : 'Änderungen'} übernehmen`}
                  </button>
                </div>
              );
            })() : (
              <button
                onClick={onResolve}
                disabled={modal.saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {modal.saving ? 'Speichern...' : 'Erledigt markieren'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// D) CHARTS SECTION - Top 5 Maschinen & Strecken
interface ChartsSectionProps {
  machineRows: MachineKpiRow[];
  lineRows: LineKpiRow[];
  loading: boolean;
}

function ChartsSection({ machineRows, lineRows, loading }: ChartsSectionProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border p-4 animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-48 mb-4" />
          <div className="h-48 bg-slate-200 rounded" />
        </div>
        <div className="bg-white rounded-lg border p-4 animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-48 mb-4" />
          <div className="h-48 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  // Prepare chart data - sorted descending, with fullName for tooltip
  // Kurzlabels via Helper (keine Ellipsis, keine Prefixe)
  const machineChartData = [...machineRows]
    .sort((a, b) => b.wsaKg - a.wsaKg)
    .slice(0, 5)
    .map(row => ({
      name: getChartLabelForMachine(row.machineName),
      fullName: row.machineName,
      value: row.wsaKg,
      id: row.machineId,
    }));

  const lineChartData = [...lineRows]
    .sort((a, b) => b.processingKg - a.processingKg)
    .slice(0, 5)
    .map(row => ({
      name: getChartLabelForLine(row.lineName),
      fullName: row.lineName,
      value: row.processingKg,
      id: row.lineId,
    }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="min-w-0">
        <HorizontalBarChart
          data={machineChartData}
          title="Top 5 Maschinen (WSA kg)"
          unit="kg"
          height={220}
          maxItems={5}
          emptyMessage="Keine WSA-Daten im Zeitraum"
        />
      </div>
      <div className="min-w-0">
        <HorizontalBarChart
          data={lineChartData}
          title="Top 5 Strecken (FL kg)"
          unit="kg"
          height={220}
          maxItems={5}
          emptyMessage="Keine Strecken-Daten im Zeitraum"
        />
      </div>
    </div>
  );
}

// E) DRILLDOWN CARDS
function DrilldownCards() {
  const cards = [
    { 
      label: 'WSA-Auswertung', 
      description: 'Detaillierte Waschgaenge nach Maschine, Kunde, Programm',
      href: '/reports/wsa', 
      icon: WashingMachine, 
      color: 'blue' 
    },
    { 
      label: 'Vorarbeiter-Auswertung', 
      description: 'Strecken-Produktion nach Linie, Kunde, Erfasser',
      href: '/reports/processing', 
      icon: Route, 
      color: 'green' 
    },
    { 
      label: 'Waschleistung', 
      description: 'WL = WSA + P25 + P50 nach Schicht und Tag',
      href: '/reports/wash-performance', 
      icon: BarChart3, 
      color: 'indigo' 
    },
  ];

  const colorMap: Record<string, { bg: string; icon: string; border: string }> = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
    green: { bg: 'bg-green-50', icon: 'text-green-600', border: 'border-green-200' },
    indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-200' },
  };

  return (
    <div>
      <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
        Auswertungen
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((item) => (
          <Link key={item.href} to={item.href}>
            <div className={`rounded-xl border ${colorMap[item.color].border} ${colorMap[item.color].bg} p-5 hover:shadow-md transition-shadow h-full`}>
              <div className="flex items-start justify-between">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-white shadow-sm`}>
                  <item.icon className={`w-6 h-6 ${colorMap[item.color].icon}`} />
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mt-4">{item.label}</h3>
              <p className="text-sm text-slate-600 mt-1">{item.description}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// F) DATA QUALITY BOX
interface DataQualityProps {
  data: {
    shiftsWithMissingTwa: number;
    totalShiftsLast7Days: number;
    disruptionsWithoutMachine: number;
    totalDisruptions: number;
    openDisruptionsOver24h: number;
    openDisruptionsOver48h: number;
    disruptionsWithoutCategory: number;
    stillstandTodayMinutes: number;
  };
}

function formatStillstand(minutes: number): string {
  if (minutes <= 0) return '0:00';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m.toString().padStart(2, '0')}min`;
}

function DataQualityBox({ data }: DataQualityProps) {
  const hasIssues = 
    data.shiftsWithMissingTwa > 0 || 
    data.openDisruptionsOver24h > 0 ||
    data.disruptionsWithoutCategory > 0;

  const hasStillstand = data.stillstandTodayMinutes > 0;

  if (!hasIssues && !hasStillstand) {
    return null;
  }

  const issueCount = (data.shiftsWithMissingTwa > 0 ? 1 : 0) +
    (data.openDisruptionsOver24h > 0 ? 1 : 0) +
    (data.openDisruptionsOver48h > 0 ? 1 : 0) +
    (data.disruptionsWithoutCategory > 0 ? 1 : 0) +
    (hasStillstand ? 1 : 0);

  return (
    <details className="group rounded-lg border border-slate-200 bg-white">
      <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        <ChevronRight className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-90" />
        <ShieldAlert className="w-4 h-4 text-slate-400" />
        <span className="text-sm font-medium text-slate-500">Datenqualitaet / Info</span>
        <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-500 rounded-full">{issueCount}</span>
      </summary>
      <div className="px-4 pb-4 space-y-4">
        {/* Stillstand heute */}
        {hasStillstand && (
          <div className="bg-slate-100 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-700">Stillstand heute</span>
              </div>
              <span className="text-xl font-bold text-slate-900">
                {formatStillstand(data.stillstandTodayMinutes)}
              </span>
            </div>
          </div>
        )}

        {/* Datenqualitaet Warnungen */}
        {hasIssues && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {data.shiftsWithMissingTwa > 0 && (
                <div className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="text-amber-800 font-medium">{data.shiftsWithMissingTwa}</p>
                  <p className="text-amber-600 text-xs">Schichten ohne TWA (7 Tage)</p>
                </div>
              )}
              {data.openDisruptionsOver24h > 0 && (
                <div className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="text-amber-800 font-medium">{data.openDisruptionsOver24h}</p>
                  <p className="text-amber-600 text-xs">Offene Stoerungen &gt;24h</p>
                </div>
              )}
              {data.openDisruptionsOver48h > 0 && (
                <div className="bg-white rounded-lg p-3 border border-red-100">
                  <p className="text-red-800 font-medium">{data.openDisruptionsOver48h}</p>
                  <p className="text-red-600 text-xs">Offene Stoerungen &gt;48h</p>
                </div>
              )}
              {data.disruptionsWithoutCategory > 0 && (
                <div className="bg-white rounded-lg p-3 border border-amber-100">
                  <p className="text-amber-800 font-medium">{data.disruptionsWithoutCategory}</p>
                  <p className="text-amber-600 text-xs">Stoerungen ohne Kategorie</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </details>
  );
}
